// Mirrors mission-x-api api/equipment/equipment.workflows.js - which workflows each
// Equipment tier runs and who they can notify. The API is the gate; this is display.

export const EQUIPMENT_TIER_RANK = { free: 0, core: 1, scale: 2 }
export const EQUIPMENT_TIER_NAMES = { free: "Free", core: "Core", scale: "Scale" }

export const EQUIPMENT_WORKFLOWS = [
    {
        id: "dueAlerts",
        minTier: "free",
        title: "Due-date and expiry alerts",
        description:
            "30 days, 7 days and on the day for service, calibration, warranty, insurance, inspection and lease end.",
        recipients: true,
    },
    {
        id: "checkout",
        minTier: "core",
        title: "Check-out / check-in",
        description:
            "Hand units out until an expected return date. Overdue holders are reminded daily from the day after.",
    },
    {
        id: "maintenance",
        minTier: "core",
        title: "Maintenance requests",
        description:
            "Anyone can report an issue. High and Out of service put the unit Under Maintenance and block bookings until resolved.",
        recipients: true,
    },
    {
        id: "blocking",
        minTier: "core",
        title: "Calibration and service blocking",
        description:
            "Units past their calibration or service date can't be booked until the date is updated. Project managers with bookings are told.",
        recipients: true,
    },
    {
        id: "unbilled",
        minTier: "core",
        title: "Unbilled usage alerts",
        description:
            "Weekly: billable bookings in closed months with no client invoice for the project, at the bill rate.",
        recipients: true,
    },
    {
        id: "approvals",
        minTier: "core",
        title: "Booking approvals",
        description:
            "Units marked \"Bookings need approval\" go to their approver; pending bookings show as tentative. Approvers are reminded after 24 hours.",
    },
    {
        id: "accessRequests",
        minTier: "scale",
        title: "Restricted access requests",
        description:
            "People can ask for access to restricted equipment. Compliance approvers decide, optionally until a date.",
    },
    {
        id: "escalations",
        minTier: "scale",
        title: "Escalations",
        description:
            "Overdue returns, open maintenance and pending approvals waiting longer than the set days go to the custodian's manager or the people you pick.",
        recipients: true,
    },
    {
        id: "digest",
        minTier: "scale",
        title: "Weekly digest",
        description:
            "Monday email: overdue returns, open maintenance, upcoming calibrations and expiries, blocked equipment and unbilled usage.",
        recipients: true,
    },
]

// Approval rules (by type / booking value, several approvers in order) come with Scale.
export const EQUIPMENT_APPROVAL_RULES_MIN_TIER = "scale"

export const EQUIPMENT_RECIPIENT_GROUPS = [
    { id: "custodian", title: "Custodian" },
    { id: "equipmentAdmins", title: "Equipment admins" },
    { id: "projectManagers", title: "Project managers" },
    { id: "billing", title: "Billing (can manage invoices)" },
    { id: "manager", title: "Custodian's manager" },
]

export const EQUIPMENT_REQUEST_SEVERITIES = ["Low", "High", "Out of service"]
export const EQUIPMENT_BLOCKING_SEVERITIES = ["High", "Out of service"]

export const EQUIPMENT_TIER_CHANNELS = {
    free: ["inApp"],
    core: ["inApp", "email"],
    scale: ["inApp", "email"],
}

export const tierAtLeast = (tier, minTier) =>
    tier in EQUIPMENT_TIER_RANK && EQUIPMENT_TIER_RANK[tier] >= EQUIPMENT_TIER_RANK[minTier]

/**
 * Whether a workflow runs right now, from GET /equipment/workflows.
 */
export const isWorkflowOn = (workflows, id) => Boolean(workflows?.workflows?.[id]?.enabled)

// The equipment notification types people can mute - mirrors NOTIFICATION_TYPE in the API.
export const EQUIPMENT_NOTIFICATION_TYPES = {
    equipment_due: "Due dates and expiries",
    equipment_checkout_overdue: "Overdue returns",
    equipment_maintenance_reported: "Maintenance reported / assigned",
    equipment_maintenance_resolved: "Maintenance resolved",
    equipment_booking_blocked: "Booked equipment blocked",
    equipment_unbilled: "Unbilled usage",
    equipment_booking_approval: "Booking approval requests",
    equipment_booking_decision: "Booking approved / rejected",
    equipment_access_request: "Access requests",
    equipment_access_decision: "Access granted / refused",
    equipment_escalation: "Escalations",
    equipment_digest: "Weekly digest (email)",
}

export const isEquipmentNotification = (type) => typeof type === "string" && type.startsWith("equipment_")
